/**
 * kumo.auth の「外部 ID とメールの確認・再設定」部分（0013 identity）。auth.ts がこの戻り値を展開して公開する。
 *
 *   signInWithProvider('google' | 'discord' | 'apple' | 'x' | 'kumodeck', { mode: 'popup' | 'redirect', redirectUrl? })
 *     'kumodeck' = KUMODeck でログイン（2026-10-07 core/kumodeck-signin）。作り手は鍵を登録しない（on にするだけ）。往復は他と同じ
 *     （サーバーの start が KUMODeck のアカウントの場所の URL を返す）。アプリ内ブラウザでもメールのコードで入れるので止めない
 *   linkProvider(provider, opts?)          いまのプレイヤー（ゲスト）に Google 等を連携 = 進捗はそのまま
 *   completeRedirectSignIn()               リダイレクトで戻ってきたページで結果を受け取る（init が自動で呼ぶ）
 *   sendPasswordReset(email, redirectUrl?) / resetPassword(token, pw)
 *   verifyEmail(token) / resendVerification(redirectUrl?)
 *   pendingAction()                        メールのリンクで開かれた時の { type: 'reset_password', token }（ゲームが入力欄を出す）
 *
 * OAuth の往復（サーバー側は apps/server/src/modules/players/oauth/flow.ts）:
 *   1. verifier（乱数）を作り、sha256 を challenge として start に送る → プロバイダの URL
 *   2. ポップアップ（既定）かページ遷移でプロバイダへ。戻り先はゲームのページ（許可リスト = config の auth.redirectUrls）
 *   3. 戻ってきた URL の fragment `#auth_code=…`（旧名 `#kumo_code=`）を verifier と一緒に exchange → 通常のセッション
 *   verifier はこのブラウザにしか無い = 誰かに途中のリンクを踏まされても、そのコードを自分のセッションに換えられない
 *
 * X のアプリ内ブラウザ（2026-09-27 Research の X 調査 §2）: X / Google のサインインは始めず、KumoError('signin_needs_browser') を返す。
 *   X … 認可ページ（x.com/i/oauth2/authorize）は X が WebView から外へ追い出し（X スタッフ回答 247123）、未ログインなら 404 になる（262920）。
 *        認可が Safari / Chrome 側で終わると、verifier（このタブの sessionStorage）があるアプリ内ブラウザに戻れず交換に失敗する
 *   Google … 埋め込みの WebView での OAuth を Google の方針が禁じている（disallowed_useragent）
 *   → 先に kumo.share.openInBrowser()（引き継ぎコード = 同じゲストのまま）で普段のブラウザへ移ってもらい、移った先でサインインする。
 *   Discord / Apple は従来どおりリダイレクト方式（X の追い出し・Google の禁止のような既知の問題が無い）。
 *   ネイティブアプリ（Capacitor）はアプリ内ブラウザではないので対象外（UA に Twitter を含まない）
 *
 * ポップアップの注意（store.ts の決済と同じ）: ブラウザはユーザー操作（click 等）の中で同期的に開いた窓しか許さないので、
 * await の前に空の窓を開き、URL が分かってから遷移させる。窓が開けなければ（ユーザー操作の外・ポップアップを止める
 * アプリの中のブラウザ）、mode を指定していない時は同じタブのリダイレクトに自動で切り替える（2026-10-21）。mode: 'popup' を
 * 明示した時だけ KumoError('popup_blocked')。
 * ポップアップの中ではゲームのページ（= この SDK）が読み込まれる。そこで init() が走ると、共有の localStorage の
 * セッションを回転させたりゲストを作ったりしてしまうので、init は `#auth_popup=1`（旧名 `#kumo_popup=1`）を見たら結果を開いた側へ渡して窓を閉じるだけにする。
 */

import { KumoError, type KumoCore, type KumoPlayer, type KumoSession, type SyncStorage } from './core.js';
import type { AuthPlugin } from './nativeAuth.js';
import { detectInAppBrowser } from './share.js';
import { loadChunk } from './lazy.js';
import { applyMailOptIn, mailOptInOf, type MailOptIn, type MailOptInField } from './mail.js';

export type OAuthProvider = 'google' | 'discord' | 'apple' | 'x' | 'kumodeck';

export interface ProviderSignInOptions extends MailOptInField {
  /**
   * popup（既定・ゲームの状態を失わない）/ redirect（モバイルのアプリ内ブラウザ等でポップアップが使えない時。X のアプリ内ブラウザでは既定が redirect）。
   * 指定しない時は、ポップアップが止められたら同じタブのリダイレクトに自動で切り替わる（2026-10-21）。'popup' を明示すると切り替えず popup_blocked
   */
  mode?: 'popup' | 'redirect';
  /** 戻り先。既定は今のページ（fragment を除く）。config の auth.redirectUrls に入っている必要がある（development の localhost は不要） */
  redirectUrl?: string;
  /**
   * ネイティブアプリでの経路（2026-09-26 モバイル）。既定（undefined / true）= アプリならネイティブ経路
   * （Sign in with Apple のシート / Google Credential Manager / ASWebAuthenticationSession・Custom Tabs）。
   * false = アプリ内でも Web の経路（ポップアップ）を使う（検証用。Google は WebView 内の OAuth を拒否する）
   */
  native?: boolean;
  // mailOptIn（「<ゲーム名> からのお知らせをメールで受け取る」がチェックされたときだけ渡す・mail.ts の MailOptIn）は MailOptInField から来る。
  //   ログインが成功した後に申し込む。プロバイダが確認済みと言ったメールならその場で confirmed。リダイレクト方式でも戻った後に申し込む。
  //   2026-09-29 creator/mail-close: 最初の公開では閉じている = 型に出ず、渡されても無視する（mail.ts の applyMailOptIn）。中では mailOptInOf で読む
}

export interface ProviderSignInResult {
  player: KumoPlayer;
  provider: OAuthProvider;
  /** 連携（linkProvider）の結果なら true */
  linked: boolean;
}

export type PendingAction = { type: 'reset_password'; token: string };

/** SDK が触るブラウザの部分。テストで差し替える（既定は globalThis） */
export interface BrowserLike {
  location: { href: string; hash: string; origin: string; assign(url: string): void };
  history?: { replaceState(data: unknown, unused: string, url?: string): void };
  open?(url: string, target: string, features: string): PopupLike | null;
  opener?: { postMessage(message: unknown, targetOrigin: string): void } | null;
  close?(): void;
  addEventListener?(type: 'message', fn: (e: { origin: string; data: unknown; source?: unknown }) => void): void;
  removeEventListener?(type: 'message', fn: (e: { origin: string; data: unknown; source?: unknown }) => void): void;
  sessionStorage?: SyncStorage;
  /** 窓をまたいだ受け渡し（relayResult）の置き場所。開いた側と戻ったタブで共有される（同じオリジンの localStorage） */
  localStorage?: SyncStorage;
  /** 窓の名前。SDK が開いたサインインの窓は POPUP_NAME（プロバイダを経由して戻っても保たれる = 「SDK が始めた窓」の目印） */
  name?: string;
}

export interface PopupLike {
  closed: boolean;
  location: { href: string; hash?: string };
  close(): void;
}

const MESSAGE_TYPE = 'kumo:oauth';
/** サインインの窓の名前。window.open の target = 開いた窓の window.name（handOffPopupResult が「SDK が開いた窓か」を見る） */
const POPUP_NAME = 'kumo-oauth';
/** メールのリンクの auth_action で SDK が扱う値。これ以外はゲーム自身の fragment とみなして触らない */
const MAIL_ACTIONS = new Set(['verify_email', 'reset_password']);
/** ポップアップの確認間隔。閉じられたか・同一オリジンに戻ったか（fragment が読めるか）を見る */
const POLL_MS = 250;
/** ポップアップ方式の待ち上限（同意画面で迷っている人を待つ長さ。サーバーの state は 10 分で失効する） */
const POPUP_TIMEOUT_MS = 10 * 60_000;
/** 閉じた窓からの postMessage を待つ猶予 */
const CLOSE_GRACE_MS = 500;
/**
 * opener も窓の名前も無くなった「戻ってきたログインのタブ」から、開いた側へ結果を渡す置き場所（localStorage のキー）
 * （2026-10-22 core/ios-signin-lock・relayResult の WHY）。オリジンごとに 1 つ（どのゲームの鍵かはタブには分からない）
 */
const RELAY_KEY = 'kumo:oauth:relay';

/**
 * プロバイダの「取り消し」の error。SDK は 'popup_closed' に寄せる（toSignInError の WHY）。
 * access_denied … サーバー（KUMODeck の同意で Cancel・各社の同意で取り消し = flow.ts が寄せる）
 */
const CANCEL_ERRORS: ReadonlySet<string> = new Set(['access_denied', 'user_cancelled_authorize', 'cancelled']);

/**
 * 戻りの error をゲームへ返す KumoError にする。
 * 取り消し（CANCEL_ERRORS）は 'popup_closed'（details.reason = 'cancelled'）に寄せる（2026-10-22 core/ios-signin-lock）。
 * WHY 新しいコード（'signin_cancelled'）にしないか: ゲームの helper（skills の user-account.js）は知っているコードだけを
 *   画面の結果にして、それ以外は投げ直す → ゲームは「Something went wrong. Please try again.」を出す
 *   （Kumo Studio ３の報告: 確認のページで Cancel → この文）。helper の写し（templates・plugins/kumodeck）は審査中で変えられない。
 *   popup_closed なら helper は今のままで「Sign-in was not finished.」= 取り消しに合う文。窓を閉じたのと同じく「やめた」なので意味も近い。
 *   取り消しかどうかを分けたいゲームは details.reason を見る
 */
export function toSignInError(error: string, provider: string): KumoError {
  if (CANCEL_ERRORS.has(error)) return new KumoError(0, 'popup_closed', `Sign in with ${provider} was cancelled`, { reason: 'cancelled', provider });
  return new KumoError(0, error, `Sign in with ${provider} failed: ${error}`);
}

/**
 * X のアプリ内ブラウザでは始めないプロバイダ（上の冒頭の WHY）。
 * エラーの details.continueInBrowser = true を見て、ゲームは「ブラウザで開く」の案内（kumo.share.openInBrowser() の url）を出す
 */
const NEEDS_REAL_BROWSER: ReadonlySet<OAuthProvider> = new Set(['x', 'google']);

function defaultBrowser(): BrowserLike | null {
  const w = globalThis as unknown as { window?: BrowserLike };
  return w.window && w.window.location ? w.window : null;
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return b64url(bytes);
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function challengeOf(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

/**
 * 戻り先の fragment の名前（PRIN-07・2026-09-27）。サーバーは `auth_code` / `auth_error` / `auth_provider` / `auth_popup` /
 * `auth_action` / `auth_token` を付ける（URL はプレイヤーのアドレスバー・メールのリンクに見える = 段階 ① では Kumo の名前を出さない）。
 * 以前の `kumo_…` も当面受ける（送信済みのメールのリンク・移行中のサーバー）。読んだ後は内部の名前（kumo_…）に揃えて返すので、
 * 呼ぶ側は `params.get('kumo_code')` のままでよい。
 *
 * ゲームの fragment を壊さない（2026-09-27 リーダー指示）: auth_code / auth_token はありふれた名前で、ゲーム自身が
 * `#auth_token=…` を使っていることがある。ここは名前を拾うだけで、「読んで消す」のは SDK が始めた流れがあるときだけ:
 *   - リダイレクトの戻り: このタブに verifier が保存されているとき（doComplete）
 *   - ポップアップの戻り: SDK が開いた窓（window.name = POPUP_NAME）のとき（handOffPopupResult）
 *   - メールのリンク: 開始の記録が無いので、auth_action が MAIL_ACTIONS のときだけ（doComplete）
 */
const FRAGMENT_KEYS = ['code', 'error', 'provider', 'popup', 'action', 'token'] as const;

function parseFragment(hash: string): URLSearchParams | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!h) return null;
  const raw = new URLSearchParams(h);
  const out = new URLSearchParams();
  for (const k of FRAGMENT_KEYS) {
    const v = raw.get(`auth_${k}`) ?? raw.get(`kumo_${k}`);
    if (v !== null) out.set(`kumo_${k}`, v);
  }
  // 知っている名前が 1 つも無ければゲーム自身の fragment（触らない）
  return [...out.keys()].length ? out : null;
}

const stripHash = (href: string) => href.split('#')[0]!;

/** リダイレクトの保存に入れる mailOptIn（無ければキーごと入れない） */
const optInPart = (optIn: MailOptIn | undefined) => (optIn ? { mailOptIn: optIn } : {});

interface PendingRedirect {
  verifier: string;
  provider: OAuthProvider;
  /** リダイレクトで戻った後に申し込む（ページが遷移するので options を覚えておく） */
  mailOptIn?: MailOptIn;
}

export function createIdentity(core: KumoCore, browserOverride?: BrowserLike | null) {
  const browser = () => (browserOverride === undefined ? defaultBrowser() : browserOverride);
  // リダイレクト方式の verifier の置き場所。sessionStorage = このタブだけ・閉じれば消える。使えなければ SDK の保存領域
  const pendingKey = core.storageKey('oauth');
  const pendingStore = (): SyncStorage => {
    try {
      const s = browser()?.sessionStorage;
      if (s) return s;
    } catch {
      /* サードパーティ iframe 等 */
    }
    return core.storage;
  };

  let completion: Promise<ProviderSignInResult | null> | null = null;
  let pending: PendingAction | null = null;

  /** 画面の URL から #kumo_… を消す（再読み込みで同じコードを使おうとしない・共有された URL にトークンを残さない） */
  const clearFragment = () => {
    const b = browser();
    if (b?.history) b.history.replaceState(null, '', stripHash(b.location.href));
  };

  const exchange = async (code: string, verifier: string) => {
    const r = await core.request<KumoSession & { provider: OAuthProvider; linked: boolean }>('POST', '/v1/auth/oauth/exchange', { code, verifier }, { auth: false });
    core.adoptSession(r);
    return { player: r.player, provider: r.provider, linked: r.linked };
  };

  const startUrl = async (provider: OAuthProvider, redirectUrl: string, challenge: string, mode: 'popup' | 'redirect', link: boolean) => {
    const body = { redirectUrl, challenge, mode };
    // 連携はこの端末のリフレッシュトークンを添える（SEC-ACCESS-02）
    const r = link
      ? await core.withDeviceProof((proof) => core.request<{ url: string }>('POST', `/v1/auth/oauth/${provider}/start`, { ...body, ...proof }))
      : await core.request<{ url: string }>('POST', `/v1/auth/oauth/${provider}/start`, body, { auth: false });
    return r.url;
  };

  const run = async (provider: OAuthProvider, opts: ProviderSignInOptions, link: boolean): Promise<ProviderSignInResult> => {
    // ネイティブアプリ（Capacitor）: WebView 内の OAuth は Google が禁止し、ポップアップも動かないので別経路（nativeAuth.ts）
    // nativeAuth.ts は使うときだけ読む（/sdk/nativeAuth-<hash>.mjs・2026-09-27 sdk-light）。Web のプレイヤーには要らない 2KB。
    // 読む（= await する）のはネイティブのプラグインがあるときだけ。Web の経路は下のポップアップを await より前に開く必要がある
    // （ユーザー操作の中でないとブロックされる）ので、ここで await を挟まない
    if (opts.native !== false && core.native?.plugin<AuthPlugin>('KumoAuth')) {
      const { nativeSignIn } = await loadChunk('nativeAuth', () => import('./nativeAuth.js'), core.apiUrl);
      const nativeRun = nativeSignIn(core, { startUrl, exchange, randomVerifier, challengeOf, parseFragment });
      if (nativeRun) return nativeRun(provider, opts, link);
    }
    const b = browser();
    if (!b) throw new KumoError(0, 'no_browser', 'Provider sign-in needs a browser window');
    const inApp = detectInAppBrowser((b as { navigator?: { userAgent?: string } }).navigator?.userAgent);
    // X / Google はアプリ内ブラウザでは完了できない見込みが高い → サーバーを呼ぶ前（state を作らない・窓を開かない）に止める。
    // mode を明示しても止める（redirect でも popup でも、戻り先に verifier が無い・Google が弾くのは同じ）
    if (inApp && NEEDS_REAL_BROWSER.has(provider)) {
      throw new KumoError(
        0,
        'signin_needs_browser',
        `Sign in with ${provider === 'x' ? 'X' : 'Google'} does not work inside the X app's browser. Open this game in your browser (kumo.share.openInBrowser()) and sign in there; your progress moves with you.`,
        { inAppBrowser: inApp, provider, continueInBrowser: true, continueAsGuest: true }
      );
    }
    // それ以外のプロバイダは、アプリ内ブラウザ（WebView）ではポップアップが開かない・開いても戻れないことが多いので、既定をリダイレクトにする（share.ts）
    const mode = opts.mode ?? (inApp ? 'redirect' : 'popup');
    const redirectUrl = opts.redirectUrl ?? stripHash(b.location.href);
    const verifier = randomVerifier();

    const redirect = async (): Promise<never> => {
      const challenge = await challengeOf(verifier);
      const url = await startUrl(provider, redirectUrl, challenge, 'redirect', link);
      pendingStore().setItem(pendingKey, JSON.stringify({ verifier, provider, ...optInPart(mailOptInOf(opts)) } satisfies PendingRedirect));
      b.location.assign(url);
      // ページが遷移するので解決しない。戻ってきたページで init() → completeRedirectSignIn() が結果を受け取る
      return new Promise<never>(() => undefined);
    };

    if (mode === 'redirect') return redirect();

    // await より前に窓を開く（ユーザー操作の中でないと開けない）。開けない環境は null を返すか、例外を投げる（埋め込みのブラウザの一部）
    let popup: PopupLike | null;
    try {
      popup = b.open?.('', POPUP_NAME, 'popup,width=500,height=700') ?? null;
    } catch {
      popup = null;
    }
    if (!popup) {
      // ポップアップが止められたら、同じタブでログインの画面へ移って戻る形（redirect）に自動で切り替える（2026-10-21）。
      // WHY: founder が Claude のデスクトップアプリの中のブラウザで「Continue with KUMODeck」を押したら止まった（クリックの中で
      //   直接呼んでいても）。UA ではアプリの中と分からない（detectInAppBrowser が拾えない）ので既定は popup を選ぶ →
      //   以前は popup_blocked を投げるだけで、作り手が redirect の分岐を書かない限り先へ進めなかった。
      //   X 以外のアプリの中のブラウザ（Discord・Instagram 等）でも同じことが起きうる。「AI に頼むだけでログインが付く」には
      //   作り手の手間なしでどこでも入れる必要がある。
      // 戻ってきた時: redirectUrl は今のページ・verifier はこのタブの sessionStorage（init の completeRedirectSignIn が交換する）。
      //   ゲームの保存は既存の自動保存（gamedata.ts の pagehide で flush）が遷移の前に送る。
      // mode: 'popup' を明示したときは切り替えない（従来どおり popup_blocked）: 作り手が「このページを離れたくない」と選んだ
      //   （保存していない状態がある等）。勝手にページを移すと、その約束を破って遊んでいた状態を失わせうる。
      //   既定（mode なし）の作り手と skills の helper（mode を渡さない）は自動で切り替わる。
      // start はここから await してよい: ページの遷移（location.assign）はユーザー操作の中でなくても止められない
      if (opts.mode === undefined) return redirect();
      throw new KumoError(0, 'popup_blocked', 'The sign-in window was blocked. Call this from a click handler, or use { mode: "redirect" }.');
    }
    let url: string;
    try {
      url = await startUrl(provider, redirectUrl, await challengeOf(verifier), 'popup', link);
    } catch (e) {
      popup.close();
      throw e;
    }
    popup.location.href = url;
    const params = await waitForPopup(b, popup);
    const error = params.get('kumo_error');
    if (error) throw toSignInError(error, provider);
    const code = params.get('kumo_code');
    if (!code) throw new KumoError(0, 'provider_error', 'Sign-in window returned without a result');
    return exchange(code, verifier);
  };

  /**
   * ポップアップの結果を待つ。2 つの経路のどちらか早い方:
   *   - ポップアップ内の SDK（init）が postMessage で fragment を渡してくる
   *   - 同一オリジンに戻ったポップアップの location.hash を直接読む（ポップアップ側で SDK が読み込まれないページでも動く）
   * 途中の他オリジン（プロバイダ）では location の読み取りが例外になるので無視する
   */
  const waitForPopup = (b: BrowserLike, popup: PopupLike) =>
    new Promise<URLSearchParams>((resolve, reject) => {
      const started = Date.now();
      let closedAt: number | null = null;
      const finish = (fn: () => void) => {
        clearInterval(timer);
        b.removeEventListener?.('message', onMessage);
        fn();
      };
      // 窓の外からの受け渡し（relayResult）。この待ちが始まった後に置かれたものだけ（前の往復の残りを拾わない）。
      // コードは verifier と組でないと交換できない（下の受け取りの検査と同じ）ので、他のタブの結果を拾っても乗っ取りにはならない
      const relayed = (): URLSearchParams | null => {
        const store = relayStore(b);
        const raw = store?.getItem(RELAY_KEY);
        if (!raw) return null;
        try {
          const r = JSON.parse(raw) as { fragment?: string; at?: number };
          if (typeof r.fragment !== 'string' || typeof r.at !== 'number' || r.at < started) return null;
          store!.removeItem(RELAY_KEY);
          return parseFragment(r.fragment);
        } catch {
          return null;
        }
      };
      // 受け取りの検査（2026-10-07 管理の確認・KUMODeck でログイン）:
      //   origin … ゲーム自身のオリジンだけ（ポップアップの中の SDK も宛先を自分のオリジンに限って送る = handOffPopupResult）
      //   source … この呼び出しで開いた窓からだけ（同じオリジンの別の窓・iframe のメッセージで結果を差し替えさせない）
      //   state  … 届いた auth_code は、このタブだけが持つ verifier と組でないと交換できない（サーバーの exchange が照合）
      //            = 他の往復のコードを差し込まれても、このタブのセッションにはならない（state の役をこの verifier が持つ）
      const onMessage = (e: { origin: string; data: unknown; source?: unknown }) => {
        const d = e.data as { type?: string; fragment?: string } | null;
        if (e.origin !== b.location.origin || d?.type !== MESSAGE_TYPE || typeof d.fragment !== 'string') return;
        if (e.source != null && e.source !== popup) return;
        const p = parseFragment(d.fragment);
        if (p) finish(() => resolve(p));
      };
      b.addEventListener?.('message', onMessage);
      const timer = setInterval(() => {
        let hash: string | undefined;
        try {
          hash = popup.location.hash;
        } catch {
          hash = undefined; // まだプロバイダ（別オリジン）にいる
        }
        const p = (hash ? parseFragment(hash) : null) ?? relayed();
        if (p) {
          popup.close();
          return finish(() => resolve(p));
        }
        // 閉じたのを見ても少し待つ: ポップアップ側の SDK は postMessage の直後に自分で閉じるので、
        // メッセージの配送より先に「閉じた」を観測することがある。
        // 「閉じた」と決めるのはこのタブが見えている間だけ（2026-10-22 core/ios-signin-lock）: iOS の Safari では窓が別のタブで、
        //   プレイヤーがそちらにいる間このタブは裏（hidden）。窓との繋がりが切れて closed に見えても、まだログインの途中かもしれない。
        //   戻ってきた（visible）後に猶予を待ち、それでも結果（postMessage・relay）が無ければ popup_closed
        if (popup.closed && docOf(b)?.visibilityState !== 'hidden') {
          closedAt ??= Date.now();
          if (Date.now() - closedAt >= CLOSE_GRACE_MS) finish(() => reject(new KumoError(0, 'popup_closed', 'The sign-in window was closed before finishing')));
          return;
        }
        if (Date.now() - started > POPUP_TIMEOUT_MS) {
          popup.close();
          finish(() => reject(new KumoError(0, 'timeout', 'Sign-in took too long')));
        }
      }, POLL_MS);
    });

  const verifyEmail = async (token: string): Promise<KumoPlayer> => {
    const r = await core.request<{ player: KumoPlayer }>('POST', '/v1/auth/email/verify', { token }, { auth: false });
    // 確認したのが今のプレイヤーなら表示を更新（別端末でリンクを開いた場合は他人かもしれないので触らない）
    if (core.player?.id === r.player.id) core.updatePlayer(r.player);
    return r.player;
  };

  const doComplete = async (): Promise<ProviderSignInResult | null> => {
    const b = browser();
    const params = b ? parseFragment(b.location.hash) : null;
    if (!b || !params) return null;
    const action = params.get('kumo_action');
    const token = params.get('kumo_token');
    if (action && token && MAIL_ACTIONS.has(action)) {
      clearFragment();
      if (action === 'verify_email') {
        // メールのリンクで開かれた = 確認するのが目的。失敗（期限切れ等）はゲームを止めないよう握る
        await verifyEmail(token).catch((e) => console.warn('[game-backend] email verification failed', e));
      } else if (action === 'reset_password') {
        pending = { type: 'reset_password', token };
      }
      return null;
    }
    const code = params.get('kumo_code');
    const error = params.get('kumo_error');
    if (!code && !error) return null;
    // このタブで始めたリダイレクトが無ければゲーム自身の fragment（#auth_code 等）かもしれない → 触らない。
    // 以前は消して missing_verifier を投げていたが、ゲームの画面を壊しうるのでやめた（2026-09-27）。
    // 他のタブで始めて別タブに戻った（sessionStorage が無い）ケースはもともと交換できないので失うものは無い
    const store = pendingStore();
    const raw = store.getItem(pendingKey);
    if (!raw) return null;
    clearFragment();
    store.removeItem(pendingKey);
    const saved = JSON.parse(raw) as PendingRedirect;
    if (error) throw toSignInError(error, params.get('kumo_provider') ?? 'provider');
    return withMailOptIn(await exchange(code!, saved.verifier), saved.mailOptIn);
  };

  /** ログイン・連携が成功した後に「お知らせを受け取る」を申し込む（失敗してもログインの結果は返す = mail.ts applyMailOptIn） */
  const withMailOptIn = async (result: ProviderSignInResult, optIn: MailOptIn | undefined) => {
    await applyMailOptIn(core, optIn);
    return result;
  };

  return {
    signInWithProvider(provider: OAuthProvider, opts: ProviderSignInOptions = {}) {
      return run(provider, opts, false).then((r) => withMailOptIn(r, mailOptInOf(opts)));
    },
    linkProvider(provider: OAuthProvider, opts: ProviderSignInOptions = {}) {
      if (!core.isSignedIn) return Promise.reject(new KumoError(401, 'signed_out', 'Sign in (or start as a guest) before linking a provider'));
      return run(provider, opts, true).then((r) => withMailOptIn(r, mailOptInOf(opts)));
    },
    /**
     * リダイレクト方式から戻ったページで結果を受け取る。init() が自動で 1 回呼ぶので、ゲームは結果が欲しい時に呼べばよい
     * （何度呼んでも同じ結果。コードは一回限りなので 2 回交換しない）。戻りでなければ null
     */
    completeRedirectSignIn(): Promise<ProviderSignInResult | null> {
      completion ??= doComplete();
      return completion;
    },
    /** メールのリンクから開かれて、ゲームに入力欄を出してほしいもの（今はパスワード再設定だけ） */
    pendingAction(): PendingAction | null {
      return pending;
    },
    async sendPasswordReset(email: string, redirectUrl?: string): Promise<void> {
      // アカウントが無くても成功が返る（誰が登録しているかを漏らさない）
      await core.request('POST', '/v1/auth/password/forgot', { email, ...(redirectUrl ? { redirectUrl } : {}) }, { auth: false });
    },
    async resetPassword(token: string, password: string): Promise<void> {
      await core.request('POST', '/v1/auth/password/reset', { token, password }, { auth: false });
      if (pending?.token === token) pending = null;
    },
    verifyEmail,
    async resendVerification(redirectUrl?: string): Promise<{ alreadyVerified: boolean }> {
      return core.request<{ alreadyVerified: boolean }>('POST', '/v1/auth/email/resend', redirectUrl ? { redirectUrl } : {});
    }
  };
}

/**
 * document の SDK が読む部分。BrowserLike には入れない（authWaiting.ts の DomLike が document を別の形で求めるので、
 * 足すと auth.ts で型が合わなくなる）。navigator と同じく、ここで読む
 */
type DocLike = { visibilityState?: string; body?: { appendChild(n: unknown): unknown } | null; createElement?(tag: string): { textContent: string | null; setAttribute(k: string, v: string): void } };
const docOf = (b: BrowserLike) => (b as { document?: DocLike }).document;

/** 窓をまたいだ受け渡しの置き場所（ブラウザの localStorage）。使えなければ null（サードパーティ iframe・プライベートの制限等） */
function relayStore(b: BrowserLike): SyncStorage | null {
  try {
    return b.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * ポップアップの中で読み込まれたページ（init）が最初に呼ぶ。`#auth_popup=1`（旧名 `#kumo_popup=1`）の戻りなら結果を開いた側へ渡して窓を閉じ、true。
 * true のとき init はセッションの復元・ゲスト作成をしない（開いた側と共有の localStorage を触らない）
 *
 * opener も窓の名前も無い戻り（2026-10-22 core/ios-signin-lock）:
 *   iOS の Safari ではポップアップが新しいタブになる。Kumo Studio ３の報告では、ログインの後に SDK が開いた白いタブが残り、
 *   ゲームとして起動して（= init が鍵とセッションを取り合って）いた見込み。iOS がタブを止めて復元した後などは opener / name が
 *   失われうる（headless の WebKit = デスクトップでは残ることを確かめた。iOS の実機では未確認）。
 *   そこで `#auth_popup=1` と結果（code か error）が揃っていれば、opener / name が無くても:
 *     - 結果を localStorage（RELAY_KEY）に置く → 開いた側の waitForPopup が拾う（裏で止まっていても、戻ったときのポーリングで読める。
 *       postMessage や BroadcastChannel は止まったタブに届く保証が無いので、読みに行く形にした）
 *     - 自分は起動しない（true を返す = 復元・ゲスト作成をしない）。閉じてみて、閉じられなければ「前のタブに戻って」の 1 行を出す
 *     - URL の fragment を消す = このタブを開き直せば普通のゲームとして起動する（同じ結果を 2 回渡さない）
 *   `#auth_popup=1` + `auth_code`/`auth_error` はサーバーがポップアップの戻りにだけ付ける組なので、ゲーム自身の fragment とは
 *   ふつう重ならない。誰かがこの形のリンクを踏ませても、コードは開いた側の verifier と組でないと交換できない
 */
export function handOffPopupResult(browserOverride?: BrowserLike | null): boolean {
  const b = browserOverride === undefined ? defaultBrowser() : browserOverride;
  if (!b) return false;
  const params = parseFragment(b.location.hash);
  if (!params || params.get('kumo_popup') !== '1') return false;
  if (!params.get('kumo_code') && !params.get('kumo_error')) return false;
  const fragment = b.location.hash;
  if (b.opener && b.name === POPUP_NAME) {
    try {
      b.opener.postMessage({ type: MESSAGE_TYPE, fragment }, b.location.origin);
    } catch {
      /* 開いた側が別オリジンに移っていた等。開いた側のポーリングが fragment を読む */
    }
    b.close?.();
    return true;
  }
  // SDK が開いた窓の目印（opener + name）が無い = iOS で繋がりが切れた戻り（上の WHY）
  try {
    relayStore(b)?.setItem(RELAY_KEY, JSON.stringify({ fragment, at: Date.now() }));
  } catch {
    /* 置けなければ渡せない。下の 1 行でゲームに戻ってもらい、もう一度押してもらう */
  }
  if (b.history) b.history.replaceState(null, '', stripHash(b.location.href));
  b.close?.();
  showReturnHint(b);
  return true;
}

/**
 * 閉じられなかったタブに出す 1 行（ゲームの画面の上）。文は英語（SDK の他の文と同じ。ゲームの言語は SDK には分からない）。
 * ゲームの DOM に頼らない最小の要素（body が無ければ何もしない）
 */
function showReturnHint(b: BrowserLike) {
  const d = docOf(b);
  const el = d?.createElement?.('p');
  if (!d?.body || !el) return;
  el.textContent = 'Sign-in finished. Go back to the previous tab to keep playing. You can close this tab.';
  el.setAttribute('role', 'status');
  el.setAttribute('style', 'position:fixed;inset:0 0 auto 0;z-index:2147483647;margin:0;padding:16px;font:16px/1.4 system-ui,sans-serif;background:#111;color:#fff;text-align:center');
  d.body.appendChild(el);
}
